/**
 * TheatreCurtain — top-level proposal opening animation.
 *
 * Two velvet panels (left + right) start fully closed across the screen,
 * meet at the centre seam, and part to reveal the cinematic intro
 * underneath. A gold tassel sits at the centre seam during the closed
 * phase. Velvet shading is built from layered radial + linear gradients
 * to evoke a real stage curtain without using any image asset.
 *
 * The same keyframes are mirrored in
 * `merryflix-frontend/src/components/viewer/effects/curtain.css` so
 * admin preview and public viewer stay in lock-step.
 */
import { useState } from 'react';
import { useEffect } from 'react';
import type { OpeningProps } from './OpeningRegistry';
import './curtain.css';

export function TheatreCurtain({ active }: OpeningProps) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active) {
      setDone(true);
      return;
    }
    const t = window.setTimeout(() => setDone(true), 5300);
    return () => window.clearTimeout(t);
  }, [active]);

  // Once the panels have fully parted and faded out, leave the DOM
  // entirely. Keeping a transparent wrapper around the slide stage was
  // masking the slide content with a dark backdrop on certain
  // browsers. The parent (ProposalPreview) is responsible for any
  // z-stack ordering while the curtain is still active.
  if (done) return null;

  return (
    <div className="preview-theatre-curtain" aria-hidden="true">
      <div className="preview-theatre-curtain__valance" />
      <div className="preview-theatre-curtain__panel preview-theatre-curtain__panel--left">
        <div className="preview-theatre-curtain__panel--edge" />
      </div>
      <div className="preview-theatre-curtain__panel preview-theatre-curtain__panel--right">
        <div className="preview-theatre-curtain__panel--edge preview-theatre-curtain__panel--edge--right" />
      </div>
      <div className="preview-theatre-curtain__tassel" />
    </div>
  );
}